import React, { FC, ReactElement, useEffect, useRef, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Form, Input, message } from "antd";
import {
  LoginOutlined,
  MobileOutlined,
  SafetyCertificateOutlined,
  LockOutlined,
} from "@ant-design/icons";
import axios from "axios";

import { selectErrorMessage } from "../../redux-toolkit/auth/auth-selector";
import { resetAuthState } from "../../redux-toolkit/auth/auth-slice";
import { activateAccount } from "../../redux-toolkit/auth/auth-thunks";
import { selectSuccessMessage } from "../../redux-toolkit/user/user-selector";
import { BASE_URL } from "../../constants/urlConstants";

import "./Login.css";

type LoginMode = "otp" | "distributor";
type OtpStep = "phone" | "verify";

const ROLE_DISTRIBUTOR = "3";
const OTP_RESEND_SECONDS = 30;

// ── Helper: wipe everything except the keys we just wrote ──────────────────
const clearCacheForNewUser = (keepKeys: string[]) => {
  // 1. localStorage — remove all keys except the ones just set
  const lsKeys = Object.keys(localStorage);
  lsKeys.forEach((k) => {
    if (!keepKeys.includes(k)) localStorage.removeItem(k);
  });

  // 2. sessionStorage — clear entirely
  sessionStorage.clear();

  // 3. Clear Redux-persisted state key if present
  localStorage.removeItem("persist:root");
};

const Login: FC = (): ReactElement => {
  const dispatch = useDispatch();
  const history = useHistory();
  const params = useParams<{ code: string }>();
  const errorMessage = useSelector(selectErrorMessage);
  const successMessage = useSelector(selectSuccessMessage);

  const [mode, setMode] = useState<LoginMode>("otp");
  const [form] = Form.useForm();

  const [otpStep, setOtpStep] = useState<OtpStep>("phone");
  const [otpPhone, setOtpPhone] = useState("");
  const [otpValue, setOtpValue] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [loadingDistributor, setLoadingDistributor] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (params.code) dispatch(activateAccount(params.code));
    return () => {
      dispatch(resetAuthState());
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const switchMode = (m: LoginMode) => {
    setMode(m);
    form.resetFields();
    setOtpStep("phone");
    setOtpPhone("");
    setOtpValue("");
    setResendTimer(0);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const startResendTimer = () => {
    setResendTimer(OTP_RESEND_SECONDS);
    timerRef.current = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) { clearInterval(timerRef.current!); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  // ── Send OTP ────────────────────────────────────────────────
  const handleSendOtp = async (values: any) => {
    const phone = values.phone?.trim();
    if (!phone) return;
    setSendingOtp(true);
    try {
      await axios.post(`${BASE_URL}/api/otp/send`, { phone });
      setOtpPhone(phone);
      setOtpStep("verify");
      startResendTimer();
      message.success("OTP sent to your mobile!");
      setTimeout(async () => {
        try {
          const res = await axios.get(`${BASE_URL}/api/otp/${phone}`);
          if (res.data?.otp) {
            setOtpValue(String(res.data.otp));
            form.setFieldsValue({ otp: String(res.data.otp) });
          }
        } catch (_) {}
      }, 2000);
    } catch (err: any) {
      message.error(err?.response?.data?.message || "Failed to send OTP. Try again.");
    } finally {
      setSendingOtp(false);
    }
  };

  // ── Verify OTP ──────────────────────────────────────────────
  const handleVerifyOtp = async (values: any) => {
    setVerifyingOtp(true);
    try {
      const res = await axios.post(`${BASE_URL}/api/otp/verify`, {
        phone: otpPhone,
        otp: values.otp,
      });

      const { token, role, mobile, distributorId,distName, name } = res.data;

      // Clear all stale cache before writing new user's data
      clearCacheForNewUser([]);
      localStorage.setItem("token", token);
      localStorage.setItem("role", role);
      localStorage.setItem("distId", distributorId);
      localStorage.setItem("distName", distName);
      localStorage.setItem("mobile", mobile);
      localStorage.setItem("name", name);

      message.success("Login successful!");
      history.push("/");
    } catch (err: any) {
      message.error(err?.response?.data?.message || "Invalid OTP. Please try again.");
    } finally {
      setVerifyingOtp(false);
    }
  };

  // ── Resend OTP ──────────────────────────────────────────────
  const handleResendOtp = async () => {
    if (resendTimer > 0 || !otpPhone) return;
    setSendingOtp(true);
    try {
      await axios.post(`${BASE_URL}/api/otp/send`, { phone: otpPhone });
      startResendTimer();
      message.success("OTP resent!");
      setTimeout(async () => {
        try {
          const res = await axios.get(`${BASE_URL}/api/otp/${otpPhone}`);
          if (res.data?.otp) {
            setOtpValue(String(res.data.otp));
            form.setFieldsValue({ otp: String(res.data.otp) });
          }
        } catch (_) {}
      }, 2000);
    } catch (err: any) {
      message.error("Failed to resend OTP.");
    } finally {
      setSendingOtp(false);
    }
  };

  // ── Distributor login ────────────────────────────────────────
  const handleDistributorLogin = async (values: any) => {
    setLoadingDistributor(true);
    try {
      const res = await axios.post(`${BASE_URL}/api/finalle/login`, {
        mobile: values.mobile,
        password: values.password,
      });
      const { token, role, mobile, distributorId, distId, name } = res.data;

      // Clear all stale cache before writing new user's data
      clearCacheForNewUser([]);
      localStorage.setItem("token", token);
      localStorage.setItem("role", role);
      localStorage.setItem("mobile", mobile);
      localStorage.setItem("distributorId", distributorId);
      localStorage.setItem("distId", distId);
      localStorage.setItem("name", name);

      if (role === ROLE_DISTRIBUTOR) history.push("/");
    } catch (err: any) {
      message.error(err?.response?.data?.message || "Login failed. Check credentials.");
    } finally {
      setLoadingDistributor(false);
    }
  };

  const tabs: { key: LoginMode; label: string }[] = [
    { key: "otp",         label: "Retailer" },
    { key: "distributor", label: "Distributor" },
  ];

  return (
    <div className="lp-wrap">
      <div className="lp-card">

        {/* LOGO / TITLE */}
       <div className="lp-logo">
         <img
           src="/img/fin.png"
           alt="finllie"
           className="lp-logo-img"
         />
       </div>
        <p className="lp-subtitle">
          {mode === "otp"         && "Login with your mobile number"}
          {mode === "distributor" && "Distributor portal login"}
        </p>

        {/* MODE TABS */}
        <div className="lp-tabs">
          {tabs.map((t) => (
            <button
              key={t.key}
              className={`lp-tab ${mode === t.key ? "active" : ""}`}
              onClick={() => switchMode(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ALERTS */}
        {errorMessage && (
          <Alert message={errorMessage} type="error" showIcon className="lp-alert" />
        )}
        {successMessage && (
          <Alert message={successMessage} type="success" showIcon className="lp-alert" />
        )}

        {/* ── RETAILER / OTP MODE ── */}
        {mode === "otp" && (
          <Form form={form} layout="vertical" onFinish={otpStep === "phone" ? handleSendOtp : handleVerifyOtp}>
            {otpStep === "phone" && (
              <>
                <Form.Item
                  label="Mobile Number"
                  name="phone"
                  rules={[
                    { required: true, message: "Enter your mobile number" },
                    { pattern: /^\d{10}$/, message: "Enter valid 10-digit number" },
                  ]}
                >
                  <Input
                    prefix={<MobileOutlined />}
                    placeholder="Enter 10-digit mobile number"
                    size="large"
                    maxLength={10}
                  />
                </Form.Item>
                <Form.Item>
                  <button type="submit" className="lp-btn-primary" disabled={sendingOtp}>
                    {sendingOtp ? "Sending OTP..." : "Send OTP"}
                  </button>
                </Form.Item>
              </>
            )}

            {otpStep === "verify" && (
              <>
                <div className="lp-otp-info">
                  OTP sent to <strong>+91 {otpPhone}</strong>
                  <button type="button" className="lp-change-btn" onClick={() => setOtpStep("phone")}>
                    Change
                  </button>
                </div>

                <Form.Item
                  label="Enter OTP"
                  name="otp"
                  rules={[{ required: true, message: "Enter the OTP" }]}
                >
                  <Input
                    prefix={<SafetyCertificateOutlined />}
                    placeholder="6-digit OTP"
                    size="large"
                    maxLength={6}
                    value={otpValue}
                    onChange={(e) => setOtpValue(e.target.value)}
                  />
                </Form.Item>

                <Form.Item>
                  <button type="submit" className="lp-btn-primary" disabled={verifyingOtp}>
                    {verifyingOtp ? "Verifying..." : "Verify & Login"}
                  </button>
                </Form.Item>

                <div className="lp-resend-row">
                  {resendTimer > 0 ? (
                    <span className="lp-resend-timer">Resend in {resendTimer}s</span>
                  ) : (
                    <button
                      type="button"
                      className="lp-resend-btn"
                      onClick={handleResendOtp}
                      disabled={sendingOtp}
                    >
                      Resend OTP
                    </button>
                  )}
                </div>
              </>
            )}
          </Form>
        )}

        {/* ── DISTRIBUTOR MODE ── */}
        {mode === "distributor" && (
          <Form form={form} layout="vertical" onFinish={handleDistributorLogin}>
            <Form.Item
              label="Mobile Number"
              name="mobile"
              rules={[{ required: true, message: "Enter mobile number" }]}
            >
              <Input prefix={<MobileOutlined />} placeholder="Enter your mobile" size="large" />
            </Form.Item>

            <Form.Item
              label="Password"
              name="password"
              rules={[{ required: true, message: "Enter your password" }]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="Enter your password" size="large" />
            </Form.Item>

            <Form.Item>
              <button
                type="submit"
                className="lp-btn-primary lp-btn-dist"
                disabled={loadingDistributor}
              >
                {loadingDistributor ? "Logging in..." : "Login as Distributor"}
              </button>
            </Form.Item>
          </Form>
        )}

        {/* FOOTER */}
        {mode !== "distributor" && (
          <div className="lp-footer">
            Don't have an account?{" "}
            <Link to="/registration" className="lp-link lp-link-bold">Sign up</Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;